/** Strengere Syntax-Prüfung — keine SMTP-Zustellgarantie, aber typische Tippfehler abfangen. */
import { findLocalPartTypo } from './cutierLocalPartTypos';

export const CUTIER_EMAIL_REGEX =
  /^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

const PLACEHOLDER_FRAGMENTS = [
  'keine',
  'kein',
  'unbekannt',
  'unknown',
  'n/a',
  'na@',
  'none',
  'null',
  'test@test',
  'beispiel',
  'example.com',
  '@instagram',
  '@facebook',
  '@tiktok',
  'whatsapp',
  'telefon',
  'anrufen',
];

/** Häufige Provider-Vertipper → Stop, bevor die Mail „erfolgreich“ wirkt. */
const COMMON_DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmail.de': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'icoud.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'web.dee': 'web.de',
  'gmx.dee': 'gmx.de',
  't-onlinde.de': 't-online.de',
  'tonline.de': 't-online.de',
};

export interface CutierEmailValidation {
  ok: boolean;
  email?: string;
  reason?: string;
}

export interface ParsedBulkLead {
  email: string;
  name?: string;
  /** Mehrfach in der Liste oder mehrere Scheren pro Zeile */
  orderCount: number;
}

export interface ParsedBulkEmails {
  leads: ParsedBulkLead[];
  /** @deprecated Alias für leads.map(l => l.email) — Kompatibilität */
  validEmails: string[];
  invalidEntries: { raw: string; reason: string }[];
}

export function validateCutierEmail(rawInput: string): CutierEmailValidation {
  const raw = String(rawInput || '').trim();
  if (!raw) {
    return { ok: false, reason: 'Eintrag ist leer.' };
  }

  let candidate = raw.replace(/^mailto:/i, '').trim();

  const angle = candidate.match(/<([^>]+@[^>]+)>/);
  if (angle?.[1]) candidate = angle[1].trim();

  const embedded = candidate.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
  if (embedded?.[0]) candidate = embedded[0].trim();

  const email = candidate.toLowerCase().replace(/\s+/g, '');

  if (!email.includes('@')) {
    return { ok: false, reason: 'Keine gültige E-Mail — es fehlt das @-Zeichen.' };
  }
  if (email.startsWith('@') || email.endsWith('@')) {
    return { ok: false, reason: 'Adresse unvollständig (fehlender Name oder Domain).' };
  }
  if (email.includes('..')) {
    return { ok: false, reason: 'Adresse enthält doppelte Punkte — bitte korrigieren.' };
  }
  if (!CUTIER_EMAIL_REGEX.test(email)) {
    return { ok: false, reason: 'Format ungültig — bitte z. B. name@salon.de prüfen.' };
  }

  const lower = email.toLowerCase();
  if (PLACEHOLDER_FRAGMENTS.some((frag) => lower.includes(frag))) {
    return { ok: false, reason: 'Keine echte Adresse (Platzhalter oder Social-Media-Hinweis).' };
  }

  const domain = lower.split('@')[1] || '';
  if (!domain.includes('.') || domain.endsWith('.')) {
    return { ok: false, reason: 'Domain unvollständig — z. B. .de oder .com fehlt.' };
  }

  const typoHint = COMMON_DOMAIN_TYPOS[domain];
  if (typoHint) {
    return {
      ok: false,
      reason: `Vermutlich Vertipper — meintest du @${typoHint}? Diese Domain ist so nicht zustellbar.`,
    };
  }

  const localTypo = findLocalPartTypo(lower);
  if (localTypo) {
    return { ok: false, reason: localTypo.reason };
  }

  return { ok: true, email };
}

/**
 * Extrahiert Name + E-Mail aus einer Zeile.
 * Unterstützt: „Max Mustermann <mail@salon.de>“, „Max Mustermann, mail@salon.de“,
 * „mail@salon.de Max Mustermann“, reine Mail.
 */
export function parseLeadLine(rawLine: string): {
  email?: string;
  name?: string;
  invalidReason?: string;
  raw: string;
} {
  const raw = String(rawLine || '').trim();
  if (!raw) return { raw, invalidReason: 'Eintrag ist leer.' };

  let working = raw.replace(/^mailto:/i, '').trim();
  let name: string | undefined;

  const angle = working.match(/^(.*?)\s*<\s*([^>]+@[^>]+)\s*>\s*$/i);
  if (angle) {
    name = angle[1].replace(/^["']|["']$/g, '').trim() || undefined;
    working = angle[2].trim();
  } else {
    const emailMatch = working.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
    if (emailMatch) {
      const emailRaw = emailMatch[0];
      const before = working.slice(0, emailMatch.index ?? 0).replace(/[,;|]+$/g, '').trim();
      const after = working
        .slice((emailMatch.index ?? 0) + emailRaw.length)
        .replace(/^[,;|]+/, '')
        .trim();
      name = (before || after || '').replace(/^["']|["']$/g, '').trim() || undefined;
      working = emailRaw;
    }
  }

  const check = validateCutierEmail(working);
  if (!check.ok || !check.email) {
    return { raw, name, invalidReason: check.reason || 'Ungültige Adresse.' };
  }

  if (name) {
    // Name darf nicht nur die Mail selbst sein
    if (name.toLowerCase() === check.email || name.includes('@')) {
      name = undefined;
    }
  }

  return { raw, email: check.email, name };
}

/**
 * Findet Name+E-Mail auch in unordentlichen Listen (Excel, „Name <mail>“, CSV, Zeilenumbrüche).
 */
export function parseBulkEmailText(rawText: string): ParsedBulkEmails {
  const leadMap = new Map<string, ParsedBulkLead>();
  const invalidEntries: { raw: string; reason: string }[] = [];

  const chunks = rawText
    .split(/[\n\r]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .flatMap((line) => {
      // Mehrere Adressen in einer Zeile (Komma/Semikolon), aber „Name, mail@…“ als EIN Eintrag lassen
      if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(line) && !line.includes(';') && !line.includes('|')) {
        const emailCount = (line.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi) || []).length;
        if (emailCount <= 1) return [line];
      }
      return line.split(/[;|\t]+/).map((p) => p.trim()).filter(Boolean);
    });

  if (chunks.length === 0 && rawText.trim()) {
    invalidEntries.push({
      raw: rawText.trim().slice(0, 120),
      reason: 'Keine erkennbare E-Mail-Adresse im Text.',
    });
    return { leads: [], validEmails: [], invalidEntries };
  }

  for (const chunk of chunks) {
    // Mehrere reine Mails mit Komma trennen, wenn kein Name davor steht
    const emailCount = (chunk.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi) || []).length;
    const parts =
      emailCount > 1 && /,\s*[a-z0-9._%+-]+@/i.test(chunk)
        ? chunk.split(/,\s*(?=[a-z0-9._%+-]+@)/i).map((p) => p.trim()).filter(Boolean)
        : [chunk];

    for (const part of parts) {
      const parsed = parseLeadLine(part);
      if (!parsed.email) {
        invalidEntries.push({
          raw: parsed.raw.slice(0, 120),
          reason: parsed.invalidReason || 'Ungültige Adresse.',
        });
        continue;
      }
      const existing = leadMap.get(parsed.email);
      if (existing) {
        existing.orderCount += 1;
        if (!existing.name && parsed.name) existing.name = parsed.name;
      } else {
        leadMap.set(parsed.email, {
          email: parsed.email,
          name: parsed.name,
          orderCount: 1,
        });
      }
    }
  }

  const leads = Array.from(leadMap.values());

  return {
    leads,
    validEmails: leads.map((l) => l.email),
    invalidEntries,
  };
}

type MxCacheEntry = { ok: boolean; reason?: string; at: number };
const mxCache = new Map<string, MxCacheEntry>();
const MX_CACHE_TTL_MS = 10 * 60 * 1000;

interface DnsMxAnswer {
  Status?: number;
  Answer?: Array<{ type: number; data: string }>;
}

async function fetchMxViaDoh(domain: string): Promise<{ hasMx: boolean; lookupFailed: boolean }> {
  const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=MX`;
  try {
    const res = await fetch(url, {
      headers: { Accept: 'application/dns-json' },
    });
    if (!res.ok) return { hasMx: false, lookupFailed: true };
    const data = (await res.json()) as DnsMxAnswer;
    // Status 0 = NOERROR; Answer mit type 15 = MX
    const answers = Array.isArray(data.Answer) ? data.Answer : [];
    const hasMx = answers.some((a) => a.type === 15 && String(a.data || '').trim().length > 0);
    // Manche Domains nutzen nur A/AAAA (selten) — Status 0 ohne MX: als unsicher, aber nicht hart blocken
    if (hasMx) return { hasMx: true, lookupFailed: false };
    if (data.Status === 0) {
      // NOERROR ohne MX → Domain existiert oft trotzdem (Fallback A-Record). Zweiter Check: A-Record.
      const aUrl = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(domain)}&type=A`;
      const aRes = await fetch(aUrl, { headers: { Accept: 'application/dns-json' } });
      if (!aRes.ok) return { hasMx: false, lookupFailed: true };
      const aData = (await aRes.json()) as DnsMxAnswer;
      const hasA = (aData.Answer || []).some((a) => a.type === 1);
      return { hasMx: hasA, lookupFailed: false };
    }
    // NXDOMAIN / SERVFAIL
    return { hasMx: false, lookupFailed: false };
  } catch {
    return { hasMx: false, lookupFailed: true };
  }
}

/**
 * Prüft, ob die Domain Mail empfangen kann (MX bzw. A-Fallback via DNS-over-HTTPS).
 * Bei Lookup-Ausfall: fail-open (true), damit Offline/API-Probleme den Import nicht blockieren.
 */
export async function checkEmailDomainDeliverable(
  email: string
): Promise<{ ok: boolean; reason?: string }> {
  const check = validateCutierEmail(email);
  if (!check.ok || !check.email) {
    return { ok: false, reason: check.reason || 'Ungültige Adresse.' };
  }

  const domain = check.email.split('@')[1];
  const cached = mxCache.get(domain);
  if (cached && Date.now() - cached.at < MX_CACHE_TTL_MS) {
    return cached.ok ? { ok: true } : { ok: false, reason: cached.reason };
  }

  const { hasMx, lookupFailed } = await fetchMxViaDoh(domain);
  if (lookupFailed) {
    // Fail-open: DNS-API nicht erreichbar → Import nicht blockieren
    return { ok: true };
  }

  if (!hasMx) {
    const entry: MxCacheEntry = {
      ok: false,
      reason:
        'Diese Domain kann keine E-Mails empfangen (kein Mailserver gefunden). Bitte Adresse korrigieren — sonst kommt die Einladung nie an.',
      at: Date.now(),
    };
    mxCache.set(domain, entry);
    return { ok: false, reason: entry.reason };
  }

  mxCache.set(domain, { ok: true, at: Date.now() });
  return { ok: true };
}

export async function filterDeliverableLeads(
  leads: ParsedBulkLead[]
): Promise<{
  deliverable: ParsedBulkLead[];
  undeliverable: { raw: string; reason: string }[];
}> {
  const deliverable: ParsedBulkLead[] = [];
  const undeliverable: { raw: string; reason: string }[] = [];
  const domainMemo = new Map<string, { ok: boolean; reason?: string }>();

  for (const lead of leads) {
    const domain = lead.email.split('@')[1];
    let result = domainMemo.get(domain);
    if (!result) {
      result = await checkEmailDomainDeliverable(lead.email);
      domainMemo.set(domain, result);
    }
    if (result.ok) {
      deliverable.push(lead);
    } else {
      undeliverable.push({
        raw: lead.name ? `${lead.name} <${lead.email}>` : lead.email,
        reason: result.reason || 'Domain nicht zustellbar.',
      });
    }
  }

  return { deliverable, undeliverable };
}
